import React from 'react';
import PropTypes from 'prop-types';
import { Container } from 'reactstrap';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { Waypoint } from 'react-waypoint';

import {
  getTargetFileFormats,
  getFileExtension,
} from '../../../../helpers/Files';
import {
  getCollectionDestination,
  normalizePageVisibilityMode,
} from '../../../../helpers/PageSections';
import {
  usePageId,
  usePageRouteId,
  usePageIsEditing,
  usePageBasePath,
  usePageVisibilityMode,
  usePageAllowedCollectionUuids,
} from '../../../../helpers/PageIdContext';

import Loading from '../../../Loading';
import formatIntl from '../Common/formatIntl';
import SectionHeader from '../Common/SectionHeader';
import SectionHeadingField from '../Common/SectionHeadingField';
import ClearableNumberInput from '../Common/ClearableNumberInput';
import { withLayout } from '../PuckLayout';
import { useLiveCollections } from './useLiveCollections';
import { ConnectedCollectionPicker } from './CollectionPicker';
import {
  CollectionDownloadField,
  FeatureCollectionsRadioField,
  FeaturedCountField,
} from './PuckCollectionFields';
import {
  PuckCollectionGridView,
  PuckCollectionFeaturedView,
  PuckCollectionJumbotronView,
} from './PuckCollectionViews';

const PuckCollection = ({
  view,
  columns,
  selectedCollections,
  isLoggedIn,
  featureCollections,
  featuredCount,
  page_visibility_mode: pageVisibilityMode,
  sectionHeading,
  puck,
  allowCollectionDownload,
}) => {
  const intl = useIntl();
  const pageId = usePageId();
  const pageRouteId = usePageRouteId();
  const pageBasePath = usePageBasePath();
  const contextVisibilityMode = usePageVisibilityMode();
  const allowedCollectionUuids = usePageAllowedCollectionUuids();
  const effectivePageVisibilityMode = normalizePageVisibilityMode(
    pageVisibilityMode || contextVisibilityMode,
  );
  const isEditingFromContext = usePageIsEditing();
  const isEditing = typeof puck?.isEditing === 'boolean' ? puck.isEditing : isEditingFromContext;
  const {
    liveCollections,
    loading,
    loadingMore,
    hasMore,
    loadMore,
  } = useLiveCollections(selectedCollections, pageId, isEditing, allowedCollectionUuids);
  const effectiveColumns = columns > 0 ? columns : 3;
  const selectedCount = selectedCollections?.length || 0;
  const showCollectionsInfo = isEditing && selectedCount < effectiveColumns;
  const getCollectionTargetUrl = (collection) => getCollectionDestination({
    collection,
    pageVisibilityMode: effectivePageVisibilityMode,
    pageBasePath,
    pageRouteId,
    isLoggedIn,
  });

  const buildPhotos = (collections) => collections.map((collection) => {
    const fillImageUrl = (
      collection.featured_file?.urls?.original &&
      getTargetFileFormats().includes(getFileExtension(collection.featured_file?.urls?.original))
    )
      ? collection.featured_file?.urls?.original
      : collection.featured_file?.urls?.preview;
    const featuredFileSrc = columns === 1 ?
      fillImageUrl :
      collection.featured_file?.urls?.thumbnailx2;
    const destination = getCollectionTargetUrl(collection);

    return {
      src: featuredFileSrc
        ? `${featuredFileSrc}#c=${collection.uuid}`
        : null,
      width: 300,
      height: 200,
      files: collection.files,
      title: collection.title,
      subtitle: collection.subtitle,
      targeturl: destination,
      customUrl: !isLoggedIn && destination === collection.custom_url,
      collectionUuid: collection.uuid,
      allowDownload: allowCollectionDownload,
    };
  });

  const buildPlaceholderPhotos = (count) => Array.from({ length: count }, (_, index) => ({
    isPlaceholder: true,
    key: `placeholder-${index}`,
    src: null,
    width: 300,
    height: 200,
  }));

  const buildPlaceholderCollections = (count) => Array.from({ length: count }, (_, index) => ({
    id: `placeholder-${index}`,
    uuid: `placeholder-${index}`,
    isPlaceholder: true,
    title: intl.formatMessage({ defaultMessage: 'Collection title' }),
    subtitle: intl.formatMessage({ defaultMessage: 'Collection subtitle' }),
    custom_url: '',
    featured_file: {
      urls: {},
    },
    files: 0,
  }));

  const withCollectionTargets = (collections) => collections.map((collection) => {
    const destination = getCollectionTargetUrl(collection);

    return {
      ...collection,
      targeturl: destination,
      customUrl: !isLoggedIn && destination === collection.custom_url,
    };
  });

  const withPlaceholderPhotos = (photos) => {
    if (!isEditing) return photos;

    const targetColumns = columns > 0 ? columns : 3;
    const missingCount = Math.max(0, targetColumns - photos.length);

    if (missingCount === 0) return photos;
    return [...photos, ...buildPlaceholderPhotos(missingCount)];
  };

  const withPlaceholderCollections = (items, targetCount = effectiveColumns) => {
    if (!isEditing) return items;

    const missingCount = Math.max(0, targetCount - items.length);
    if (missingCount === 0) return items;

    return [...items, ...buildPlaceholderCollections(missingCount)];
  };

  const sectionTitle = sectionHeading?.title || '';
  const sectionSubtitle = sectionHeading?.subtitle || '';
  const headerAlign = sectionHeading?.align || 'left';
  const collections = Array.isArray(liveCollections) ? liveCollections : [];

  const shouldShowFeature =
    featureCollections === true
    && featuredCount >= 1
    && collections.length > 0;

  const heroCollection = withCollectionTargets(shouldShowFeature ? collections.slice(0, 1) : []);
  const gridCollections = withCollectionTargets(
    shouldShowFeature ? collections.slice(1, featuredCount) : [],
  );
  const remainingCollections = withCollectionTargets(
    shouldShowFeature ? collections.slice(featuredCount) : collections,
  );

  if (!selectedCollections || selectedCollections.length === 0) {
    const featurePlaceholderCount = Math.max(1, featuredCount || 1);
    const shouldShowFeaturePlaceholders =
      featureCollections === true && featurePlaceholderCount >= 1;
    const featurePlaceholderCollections = buildPlaceholderCollections(featurePlaceholderCount);
    const heroPlaceholderCollection = shouldShowFeaturePlaceholders
      ? featurePlaceholderCollections.slice(0, 1)
      : [];
    const topGridPlaceholderCollections = shouldShowFeaturePlaceholders
      ? featurePlaceholderCollections.slice(1, featurePlaceholderCount)
      : [];

    const remainingPlaceholderCollections = withCollectionTargets(
      buildPlaceholderCollections(effectiveColumns),
    );
    const remainingPlaceholderPhotos = buildPlaceholderPhotos(effectiveColumns);

    return (
      <div className={classnames('landing-gallery', { 'custom-page-section': !shouldShowFeaturePlaceholders })}>
        {shouldShowFeaturePlaceholders && (
          <>
            <PuckCollectionJumbotronView
              collections={withCollectionTargets(heroPlaceholderCollection)}
              isLoggedIn={isLoggedIn}
            />
            <PuckCollectionFeaturedView
              collections={withCollectionTargets(topGridPlaceholderCollections)}
              isLoggedIn={isLoggedIn}
              allowDownload={allowCollectionDownload}
            />
            {!sectionTitle && !sectionSubtitle && (
              <div className="py-2" />
            )}
          </>
        )}

        <Container>
          {view !== 'hidden' && (
            <div className="landing-gallery-header">
              <SectionHeader
                title={sectionTitle}
                subtitle={sectionSubtitle}
                align={headerAlign}
                spacing={heroPlaceholderCollection.length > 0}
              />
            </div>
          )}

          {showCollectionsInfo && (
            <p className="center puck-component-info-text py-3">
              {intl.formatMessage({ defaultMessage: 'Use the "Add Collection" button to add a Collection to this section.' })}
            </p>
          )}

          {view === 'featured' && (
            <PuckCollectionJumbotronView
              collections={remainingPlaceholderCollections.slice(0, 1)}
              isLoggedIn={isLoggedIn}
            />
          )}

          {view === 'masonry' && (
            <PuckCollectionGridView
              photos={remainingPlaceholderPhotos}
              columns={effectiveColumns}
            />
          )}

          {view === 'grid' && (
            <PuckCollectionFeaturedView
              collections={remainingPlaceholderCollections}
              isLoggedIn={isLoggedIn}
              allowDownload={allowCollectionDownload}
            />
          )}
        </Container>
      </div>
    );
  }

  const masonryPhotos = withPlaceholderPhotos(
    buildPhotos(remainingCollections),
  );
  const remainingGridCollectionsWithPlaceholders = withPlaceholderCollections(remainingCollections);
  const remainingFeaturedCollectionsWithPlaceholders = withPlaceholderCollections(
    remainingCollections,
    1,
  );

  return (
    <div className={classnames('landing-gallery landing-gallery-relative', { 'custom-page-section': heroCollection.length === 0 })}>
      {loading && (
        <div className="custom-page-section-loading-overlay">
          <Loading />
        </div>
      )}
      {heroCollection.length > 0 && (
        <>
          <PuckCollectionJumbotronView
            collections={heroCollection}
            isLoggedIn={isLoggedIn}
          />
          <PuckCollectionFeaturedView
            collections={gridCollections}
            isLoggedIn={isLoggedIn}
          />
          {!sectionTitle && !sectionSubtitle && (
            <div className="py-2" />
          )}
        </>
      )}
      {view !== 'hidden' && (
        <Container>
          <div className="landing-gallery-header">
            <SectionHeader
              title={sectionTitle}
              subtitle={sectionSubtitle}
              align={headerAlign}
              spacing={heroCollection.length > 0}
            />
          </div>
        </Container>
      )}

      {view === 'grid' && (
        <PuckCollectionFeaturedView
          collections={remainingGridCollectionsWithPlaceholders}
          isLoggedIn={isLoggedIn}
          allowDownload={allowCollectionDownload}
        />
      )}

      {view === 'featured' && (
        <PuckCollectionJumbotronView
          collections={remainingFeaturedCollectionsWithPlaceholders}
          isLoggedIn={isLoggedIn}
        />
      )}

      {view === 'masonry' && (
        <Container>
          <PuckCollectionGridView
            photos={masonryPhotos}
            columns={effectiveColumns}
          />
        </Container>
      )}

      {loadingMore && (
        <div className="puck-collections-loading-more center">
          <Loading />
        </div>
      )}
      {hasMore && (
        <Waypoint onEnter={loadMore} bottomOffset="-200px" />
      )}
    </div>
  );
};

PuckCollection.propTypes = {
  view: PropTypes.string.isRequired,
  columns: PropTypes.number,
  selectedCollections: PropTypes.array,
  isLoggedIn: PropTypes.bool,
  featureCollections: PropTypes.bool,
  featuredCount: PropTypes.number,
  page_visibility_mode: PropTypes.string,
  sectionHeading: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    align: PropTypes.oneOf(['left', 'center', 'right']),
  }),
  puck: PropTypes.shape({
    isEditing: PropTypes.bool,
  }),
  allowCollectionDownload: PropTypes.bool,
};

PuckCollection.defaultProps = {
  selectedCollections: [],
  isLoggedIn: false,
  featureCollections: false,
  featuredCount: 5,
  page_visibility_mode: null,
  sectionHeading: null,
  puck: null,
  columns: null,
  allowCollectionDownload: false,
};

const PuckCollectionInternal = {
  fields: {
    sectionHeading: {
      type: 'custom',
      get label() { return formatIntl('Section heading'); },
      render: (props) => <SectionHeadingField {...props} />,
    },
    selectedCollections: {
      type: 'custom',
      get label() { return formatIntl('Collections'); },
      render: ({ value, onChange, field }) => (
        <ConnectedCollectionPicker value={value} onChange={onChange} label={field.label} />
      ),
    },
    featureCollections: {
      type: 'custom',
      get label() { return formatIntl('Featured Collections'); },
      options: [
        { get label() { return formatIntl('Enable'); }, value: true },
        { get label() { return formatIntl('Disable'); }, value: false },
      ],
      render: (props) => <FeatureCollectionsRadioField {...props} hint={formatIntl('Show your latest Collection as a large, full-width banner at the top of this section - like a hero image with a title and link.')} />,
    },
    featuredCount: {
      type: 'custom',
      get label() { return formatIntl('Collections in top grid'); },
      render: (props) => <FeaturedCountField {...props} hint={formatIntl('How many Collections should appear in the small grid connected to the featured Collection, including the featured one ("5" means 1 featured + 4 in the grid)')} />,
    },
    view: {
      type: 'custom',
      get label() { return formatIntl('Remaining Collections layout'); },
      options: [
        { get label() { return formatIntl('Hidden'); }, value: 'hidden' },
        { get label() { return formatIntl('Masonry'); }, value: 'masonry' },
        { get label() { return formatIntl('Grid'); }, value: 'grid' },
        { get label() { return formatIntl('Featured'); }, value: 'featured' },
      ],
      render: (props) => <FeatureCollectionsRadioField {...props} hint={formatIntl('Choose how any additional Collections below the top grid are displayed.')} />,
    },
    columns: {
      type: 'custom',
      get label() { return formatIntl('Columns'); },
      render: ({ value, onChange, field }) => (
        <ClearableNumberInput
          hint={formatIntl('Number of columns for your remaining Collections')}
          value={value}
          onChange={onChange}
          label={field.label}
          min={1}
          max={6}
          step={1}
        />
      ),
    },
    allowCollectionDownload: {
      type: 'custom',
      get label() { return formatIntl('Collection download'); },
      options: [
        { get label() { return formatIntl('Yes'); }, value: true },
        { get label() { return formatIntl('No'); }, value: false },
      ],
      render: (props) => <CollectionDownloadField {...props} />,
    },
  },

  defaultProps: {
    selectedCollections: [],
    view: 'masonry',
    columns: 3,
    featureCollections: false,
    featuredCount: 5,
    allowCollectionDownload: true,
    sectionHeading: {
      title: formatIntl('Header'),
      subtitle: formatIntl('Subheader'),
      align: 'left',
    },
    layout: {
      padding: '0px',
    },
  },

  render: (props) => <PuckCollection {...props} />,
};

export default withLayout(PuckCollectionInternal);
